import React from 'react';
import CloseIcon from '@mui/icons-material/Close';
import Cookies from 'js-cookie';
import moment from 'moment';

function EventModal({ isOpen, onClose, event, onSubmit, onDelete, onInviteResponse, loading, allEvents = [] }) {
  const [facilities, setFacilities] = React.useState([]);
  const [facilitiesLoading, setFacilitiesLoading] = React.useState(false);
  const [availableUsers, setAvailableUsers] = React.useState([]);
  const [usersLoading, setUsersLoading] = React.useState(false);
  const [formData, setFormData] = React.useState({
    title: '',
    startDatetime: '',
    endDatetime: '',
    description: '',
    location: '',
    allDay: false,
    visibility: 'private',
    facility_id: '',
    invitees: []
  });

  React.useEffect(() => {
    if (event) {
      // 既存イベントの場合、invitesからuser_idの配列を抽出
      const existingInvitees = event.invites 
        ? event.invites.map(invite => invite.user_id) 
        : [];
      
      // 日時をdatetime-local形式に変換（YYYY-MM-DD HH:MM:SS → YYYY-MM-DDTHH:MM）
      const formatDatetimeForInput = (datetime) => {
        if (!datetime) return '';
        // スペースをTに置き換え、秒を削除
        return datetime.replace(' ', 'T').slice(0, 16);
      };
      
      setFormData({
        title: event.title || '',
        startDatetime: formatDatetimeForInput(event.start_datetime),
        endDatetime: formatDatetimeForInput(event.end_datetime),
        description: event.description || '',
        location: event.location || '',
        allDay: event.all_day || false,
        visibility: event.visibility || 'private',
        facility_id: event.facility_id || '',
        invitees: existingInvitees
      });
    } else {
      const now = new Date();
      const start = new Date(now.setMinutes(0, 0, 0));
      const end = new Date(start.getTime() + 60 * 60 * 1000);
      
      setFormData({
        title: '',
        startDatetime: start.toISOString().slice(0, 16),
        endDatetime: end.toISOString().slice(0, 16),
        description: '',
        location: '',
        allDay: false,
        visibility: 'private',
        facility_id: '',
        invitees: []
      });
    }
  }, [event, isOpen]);

  // 施設一覧を取得
  React.useEffect(() => {
    const fetchFacilities = async () => {
      if (!isOpen) return;
      
      setFacilitiesLoading(true);
      try {
        const token = Cookies.get('auth_token');
        if (!token) return;

        const response = await fetch('https://system.sales-management.cloud/api/facilities.php', {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        const data = await response.json();
        if (data.status === 'success' && data.facilities) {
          setFacilities(data.facilities);
        }
      } catch (err) {
        console.error('Failed to fetch facilities:', err);
      } finally {
        setFacilitiesLoading(false);
      }
    };

    fetchFacilities();
  }, [isOpen]);

  // 時間の重複をチェックする関数
  const getConflictingUserIds = React.useCallback((startDatetime, endDatetime, isAllDay) => {
    const conflictUserIds = new Set();
    
    // datetime-local形式（YYYY-MM-DDTHH:MM）からDateオブジェクトを作成
    const parseDateTime = (dateTimeStr, isAllDay) => {
      if (isAllDay) {
        // 終日の場合は日付部分のみ
        const dateOnly = dateTimeStr.split('T')[0];
        return moment(dateOnly).startOf('day');
      } else {
        // 通常の場合
        return moment(dateTimeStr);
      }
    };

    const newStart = parseDateTime(startDatetime, isAllDay);
    const newEnd = parseDateTime(endDatetime, isAllDay);

    // 編集中のイベントIDを取得（自分のイベントは除外する）
    const currentEventId = event ? (event.event_id || event.id || null) : null;

    // 全イベントをチェック
    allEvents.forEach(existingEvent => {
      // 自分自身のイベントはスキップ
      if (currentEventId && existingEvent.event_id === currentEventId) {
        return;
      }

      const existingStart = moment(existingEvent.start_datetime);
      const existingEnd = moment(existingEvent.end_datetime);

      // 時間の重複チェック: (start1 < end2) && (start2 < end1)
      const isOverlapping = newStart.isBefore(existingEnd) && existingStart.isBefore(newEnd);

      if (isOverlapping) {
        // イベント作成者を追加
        const creatorUserId = existingEvent.creator_user_id || existingEvent.created_by;
        if (creatorUserId) {
          conflictUserIds.add(creatorUserId);
        }

        // 承諾済みの招待者を追加
        if (existingEvent.invites && Array.isArray(existingEvent.invites)) {
          existingEvent.invites.forEach(invite => {
            if (invite.status === 'accepted') {
              conflictUserIds.add(invite.user_id);
            }
          });
        }
      }
    });

    return Array.from(conflictUserIds);
  }, [allEvents, event]);

  // イベント一覧から招待可能なユーザーを抽出
  React.useEffect(() => {
    const fetchAvailableUsers = async () => {
      if (!isOpen) return;
      
      setUsersLoading(true);
      try {
        const token = Cookies.get('auth_token');
        if (!token) return;

        // イベント一覧を取得して、invitesから全ユーザーを抽出
        const response = await fetch('https://system.sales-management.cloud/api/events.php?limit=100', {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        const data = await response.json();
        
        if (data.status === 'success' && data.available_users) {
          // APIレスポンスのavailable_usersを直接使用
          let users = data.available_users.map(user => ({
            user_id: user.user_id,
            name: user.user_name,
            role: user.user_role
          }));

          // 時間重複チェックでフィルタリング
          if (formData.startDatetime && formData.endDatetime && allEvents.length > 0) {
            const conflictUserIds = getConflictingUserIds(
              formData.startDatetime, 
              formData.endDatetime, 
              formData.allDay
            );
            
            // 時間が重複しているユーザーを除外
            users = users.filter(user => !conflictUserIds.includes(user.user_id));
          }

          setAvailableUsers(users);
        }
      } catch (err) {
        console.error('Failed to fetch available users:', err);
      } finally {
        setUsersLoading(false);
      }
    };

    fetchAvailableUsers();
  }, [isOpen, formData.startDatetime, formData.endDatetime, formData.allDay, allEvents, getConflictingUserIds]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    // 開始日時の変更時、終了日時も同じ時間差を保ったままずらす
    if (name === 'startDatetime' && !formData.allDay && formData.startDatetime && formData.endDatetime && value) {
      try {
        // datetime-local形式（YYYY-MM-DDTHH:MM）を明示的にローカルタイムとしてパース
        const parseLocalDateTime = (str) => {
          const [datePart, timePart] = str.split('T');
          const [year, month, day] = datePart.split('-').map(Number);
          const [hour, minute] = timePart.split(':').map(Number);
          return new Date(year, month - 1, day, hour, minute, 0, 0);
        };
        
        const oldStart = parseLocalDateTime(formData.startDatetime);
        const oldEnd = parseLocalDateTime(formData.endDatetime);
        const newStart = parseLocalDateTime(value);
        
        // 有効な日付かチェック
        if (!isNaN(oldStart.getTime()) && !isNaN(oldEnd.getTime()) && !isNaN(newStart.getTime())) {
          // 元の時間差を計算（ミリ秒）
          const timeDiff = oldEnd.getTime() - oldStart.getTime();
          
          // 時間差が0以上の場合のみ適用
          if (timeDiff >= 0) {
            // 新しい終了時間を計算
            const newEnd = new Date(newStart.getTime() + timeDiff);
            
            // ローカルタイムゾーンで YYYY-MM-DDTHH:MM 形式に変換
            const formatLocalDateTime = (date) => {
              const year = date.getFullYear();
              const month = String(date.getMonth() + 1).padStart(2, '0');
              const day = String(date.getDate()).padStart(2, '0');
              const hour = String(date.getHours()).padStart(2, '0');
              const minute = String(date.getMinutes()).padStart(2, '0');
              return `${year}-${month}-${day}T${hour}:${minute}`;
            };
            
            setFormData(prev => ({
              ...prev,
              startDatetime: value,
              endDatetime: formatLocalDateTime(newEnd)
            }));
            return;
          }
        }
      } catch (error) {
        console.error('Error calculating time difference:', error);
      }
    }
    
    // 通常の変更処理
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  const handleDelete = () => {
    if (window.confirm('このイベントを削除しますか？')) {
      onDelete(event.id);
    }
  };

  const handleInviteeToggle = (userId) => {
    setFormData(prev => {
      const invitees = prev.invitees.includes(userId)
        ? prev.invitees.filter(id => id !== userId)
        : [...prev.invitees, userId];
      return { ...prev, invitees };
    });
  };

  if (!isOpen) return null;

  const isExistingEvent = Boolean(event && (event.event_id || event.id));

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={handleBackdropClick}
    >
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex justify-between items-center">
          <h2 className="text-xl font-bold text-gray-900">
            {isExistingEvent ? 'イベントを編集' : '新しいイベント'}
          </h2>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-full transition-colors"
          >
            <CloseIcon />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {isExistingEvent && event.invites && event.invites.length > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mb-4">
              <h3 className="text-sm font-semibold text-blue-900 mb-2">招待状況</h3>
              <div className="space-y-1">
                {event.invites.map((invite) => (
                  <div key={invite.user_id} className="flex items-center justify-between text-sm">
                    <span className="text-gray-700">{invite.user_name}</span>
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                      invite.status === 'accepted' ? 'bg-green-100 text-green-800' :
                      invite.status === 'declined' ? 'bg-red-100 text-red-800' :
                      'bg-yellow-100 text-yellow-800'
                    }`}>
                      {invite.status === 'accepted' ? '承認' :
                       invite.status === 'declined' ? '辞退' : '未応答'}
                    </span>
                  </div>
                ))}
              </div>
              {event.pending_count > 0 && (
                <p className="text-xs text-gray-600 mt-2">
                  未応答: {event.pending_count}件
                </p>
              )}
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              タイトル <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleChange}
              required
              placeholder="イベントのタイトルを入力"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="allDay"
              name="allDay"
              checked={formData.allDay}
              onChange={handleChange}
              className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
            />
            <label htmlFor="allDay" className="text-sm font-medium text-gray-700">
              終日
            </label>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                開始日時 <span className="text-red-500">*</span>
              </label>
              <input
                type={formData.allDay ? "date" : "datetime-local"}
                name="startDatetime"
                value={formData.allDay ? formData.startDatetime.split(' ')[0] : formData.startDatetime}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                終了日時 <span className="text-red-500">*</span>
              </label>
              <input
                type={formData.allDay ? "date" : "datetime-local"}
                name="endDatetime"
                value={formData.allDay ? formData.endDatetime.split(' ')[0] : formData.endDatetime}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              場所
            </label>
            <input
              type="text"
              name="location"
              value={formData.location}
              onChange={handleChange}
              placeholder="場所を入力"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {!formData.allDay && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                施設予約
              </label>
              <select
                name="facility_id"
                value={formData.facility_id}
                onChange={handleChange}
                disabled={facilitiesLoading}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">施設を選択(オプション)</option>
                {facilities.map((facility) => (
                  <option key={facility.id} value={facility.id}>
                    {facility.name}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-gray-500">
                ※ 施設を選択すると施設予約が自動で作成されます（終日イベントでは無効）
              </p>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              公開範囲
            </label>
            <select
              name="visibility"
              value={formData.visibility}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="private">非公開</option>
              <option value="team">チーム</option>
              <option value="public">公開</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              説明
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows="4"
              placeholder="イベントの説明を入力"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              招待するユーザー
            </label>
            {usersLoading ? (
              <div className="text-sm text-gray-500">読み込み中...</div>
            ) : availableUsers.length === 0 ? (
              <div className="border border-gray-300 rounded-md p-3">
                <div className="text-sm text-gray-500 mb-2">過去のイベントから招待可能なユーザーが見つかりませんでした。</div>
                <div className="text-xs text-gray-400">ユーザーIDを直接入力してください（カンマ区切り）：</div>
                <input
                  type="text"
                  value={formData.invitees.join(', ')}
                  onChange={(e) => {
                    const value = e.target.value;
                    const ids = value.split(',').map(id => id.trim()).filter(id => id && /^\d+$/.test(id)).map(id => parseInt(id));
                    setFormData(prev => ({ ...prev, invitees: ids }));
                  }}
                  placeholder="例: 2, 5, 10"
                  className="w-full mt-2 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            ) : (
              <div className="max-h-40 overflow-y-auto border border-gray-300 rounded-md p-3 space-y-2">
                {availableUsers.map((user) => (
                  <label key={user.user_id} className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-1 rounded">
                    <input
                      type="checkbox"
                      checked={formData.invitees.includes(user.user_id)}
                      onChange={() => handleInviteeToggle(user.user_id)}
                      className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">{user.name}</span>
                  </label>
                ))}
              </div>
            )}
            <p className="mt-1 text-xs text-gray-500">
              ※ 招待したいユーザーを選択してください
            </p>
          </div>

          <div className="flex justify-between pt-4">
            <div className="flex gap-2">
              {isExistingEvent && (
                <>
                  <button
                    type="button"
                    onClick={handleDelete}
                    disabled={loading}
                    className="px-6 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors disabled:bg-red-400 disabled:cursor-not-allowed"
                  >
                    削除
                  </button>
                  {event.my_status === 'pending' && onInviteResponse && (
                    <>
                      <button
                        type="button"
                        onClick={() => onInviteResponse(event.event_id, 'accepted')}
                        disabled={loading}
                        className="px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors disabled:bg-green-400 disabled:cursor-not-allowed"
                      >
                        承認
                      </button>
                      <button
                        type="button"
                        onClick={() => onInviteResponse(event.event_id, 'declined')}
                        disabled={loading}
                        className="px-6 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                      >
                        辞退
                      </button>
                    </>
                  )}
                </>
              )}
            </div>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={onClose}
                disabled={loading}
                className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors disabled:bg-gray-100 disabled:cursor-not-allowed"
              >
                キャンセル
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors font-medium disabled:bg-blue-400 disabled:cursor-not-allowed"
              >
                {loading ? '保存中...' : (isExistingEvent ? '更新' : '作成')}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

export default EventModal;
